'use client';

import type { GeneralLayout } from '@eliadmin/core/src/api/general/types/general-layout-types';
import { HtmlRenderer } from '@eliadmin/core/src/components/ui/html-renderer';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { Cross2Icon } from '@radix-ui/react-icons';
import { useEffect, useRef, useState } from 'react';
import { AiFillWarning } from 'react-icons/ai';

type TopbarNotificationProps = {
  generalLayout: GeneralLayout['data'];
};

type DismissedPayload = { content: string; dismissedAt: number };

export const TopbarNotification = ({ generalLayout }: TopbarNotificationProps) => {
  const [isVisible, setIsVisible] = useState(false);
  const trackRef = useRef<HTMLDivElement | null>(null);
  const offsetRef = useRef(0);
  const lastTimeRef = useRef<number | null>(null);

  const content = generalLayout.header?.topbar_notification ?? '';
  const STORAGE_KEY = 'ea-topbar-notification-dismissed';

  useEffect(() => {
    if (!content) {
      setIsVisible(false);
      return;
    }
    if (typeof window === 'undefined') return;

    try {
      const raw = window.localStorage.getItem(STORAGE_KEY);
      if (!raw) {
        setIsVisible(true);
        return;
      }

      const parsed = JSON.parse(raw) as DismissedPayload;
      const now = Date.now();
      const twoDaysMs = 2 * 24 * 60 * 60 * 1000;

      if (parsed.content !== content || now - parsed.dismissedAt > twoDaysMs) {
        setIsVisible(true);
        return;
      }

      setIsVisible(false);
    } catch {
      setIsVisible(true);
    }
  }, [content]);

  // Simple JS marquee over three 1280px-wide items
  useEffect(() => {
    if (!isVisible) return;
    if (!content) return;
    if (typeof window === 'undefined') return;

    const trackEl = trackRef.current;
    if (!trackEl) return;

    let frameId: number;

    const step = (timestamp: number) => {
      if (lastTimeRef.current == null) {
        lastTimeRef.current = timestamp;
      }

      const deltaSeconds = (timestamp - lastTimeRef.current) / 1000;
      lastTimeRef.current = timestamp;

      const speed = 60; // px per second
      const itemWidth = 1280; // must match CSS width of each item

      offsetRef.current -= speed * deltaSeconds;

      // When one full item has gone off to the left, wrap by one item width
      if (offsetRef.current <= -itemWidth) {
        offsetRef.current += itemWidth;
      }

      trackEl.style.transform = `translate3d(${offsetRef.current}px, 0, 0)`;

      frameId = window.requestAnimationFrame(step);
    };

    frameId = window.requestAnimationFrame(step);

    return () => {
      if (frameId) window.cancelAnimationFrame(frameId);
      lastTimeRef.current = null;
      offsetRef.current = 0;
      trackEl.style.transform = 'translate3d(0, 0, 0)';
    };
  }, [isVisible, content]);

  const handleClose = () => {
    if (typeof window !== 'undefined' && content) {
      try {
        window.localStorage.setItem(
          STORAGE_KEY,
          JSON.stringify({ content, dismissedAt: Date.now() } satisfies DismissedPayload),
        );
      } catch {
        // ignore storage errors
      }
    }
    setIsVisible(false);
  };

  if (!content || !isVisible) return null;

  return (
    <div className={cn('w-full bg-black text-sm text-white', 'ea-header__topbar-notification')}>
      <div className="flex h-[44px] w-full items-center justify-between gap-3 px-1 sm:px-2">
        <div className="relative flex flex-1 items-center overflow-hidden">
          <div
            ref={trackRef}
            className="ea-header__topbar-notification__track flex items-center justify-center gap-4"
          >
            {[0, 1, 2, 3].map(i => (
              <div
                key={i}
                className="ea-header__topbar-notification__item flex items-center gap-1 sm:gap-2"
              >
                <AiFillWarning className="h-8 w-8 flex-shrink-0 sm:h-5 sm:w-5" aria-hidden="true" />
                <HtmlRenderer
                  html={content}
                  className={cn(
                    'ea-header__topbar-notification__content whitespace-nowrap font-medium',
                  )}
                />
              </div>
            ))}
          </div>
        </div>

        <button
          type="button"
          onClick={handleClose}
          aria-label="Close topbar notification"
          className={cn(
            'inline-flex items-center justify-center text-xs text-inherit',
            'hover:opacity-70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
          )}
        >
          <Cross2Icon className="h-5 w-5" aria-hidden="true" />
        </button>
      </div>
    </div>
  );
};
